import { useState } from "react";
import { X, AlertTriangle } from "lucide-react";

const VIEWS = [
  { id: "front", label: "Front" },
  { id: "rear", label: "Back" },
  { id: "left", label: "Left side" },
  { id: "right", label: "Right side" },
];

const TYPES = ["Scratch", "Dent", "Crack", "Missing Part"];
const SEVERITIES = ["Minor", "Major"];

/** Simple vehicle outlines — one silhouette per view, drawn in CSS currentColor. */
function CarOutline({ view }) {
  const common = { fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinejoin: "round" };
  if (view === "left" || view === "right") {
    return (
      <g transform={view === "right" ? "translate(200,0) scale(-1,1)" : undefined}>
        <path {...common} d="M18 78 L30 52 Q36 40 52 38 L118 34 Q140 33 156 46 L176 60 Q184 64 184 74 L184 86 Q184 92 176 92 L24 92 Q16 92 16 86 Z" />
        <path {...common} d="M48 40 L58 62 L104 60 L102 36" />
        <path {...common} d="M112 36 L116 60 L160 58 L150 46" />
        <circle {...common} cx="56" cy="92" r="13" />
        <circle {...common} cx="150" cy="92" r="13" />
      </g>
    );
  }
  if (view === "front") {
    return (
      <g>
        <path {...common} d="M44 86 L50 50 Q54 36 70 34 L130 34 Q146 36 150 50 L156 86 Q156 94 148 94 L52 94 Q44 94 44 86 Z" />
        <path {...common} d="M58 56 L142 56" />
        <rect {...common} x="60" y="68" width="18" height="10" rx="3" />
        <rect {...common} x="122" y="68" width="18" height="10" rx="3" />
        <rect {...common} x="78" y="80" width="44" height="8" rx="3" />
      </g>
    );
  }
  return (
    <g>
      <path {...common} d="M44 86 L50 48 Q54 34 70 32 L130 32 Q146 34 150 48 L156 86 Q156 94 148 94 L52 94 Q44 94 44 86 Z" />
      <path {...common} d="M60 52 L140 52" />
      <rect {...common} x="56" y="64" width="22" height="12" rx="3" />
      <rect {...common} x="122" y="64" width="22" height="12" rx="3" />
      <rect {...common} x="82" y="82" width="36" height="8" rx="2" />
    </g>
  );
}

/**
 * v4.7.2 — 4-view click-to-pin damage inspection.
 * `existing` renders read-only (amber) pins from an earlier inspection.
 */
export default function DamageInspector({ damages, onChange, existing = [], testId = "damage-inspector" }) {
  const [draft, setDraft] = useState(null);   // { view, x, y }
  const [hover, setHover] = useState(null);   // { view, x, y } — crosshair guide

  const coords = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const p = e.touches?.[0] || e;
    return {
      x: Math.round(((p.clientX - rect.left) / rect.width) * 100),
      y: Math.round(((p.clientY - rect.top) / rect.height) * 100),
    };
  };

  const place = (view, e) => {
    const { x, y } = coords(e);
    setDraft({ view, x, y, type: TYPES[0], severity: SEVERITIES[0], notes: "" });
  };

  const commit = () => {
    onChange([...damages, {
      zone: draft.view,
      x: draft.x,
      y: draft.y,
      type: draft.type,
      severity: draft.severity,
      notes: draft.notes.trim(),
      timestamp: new Date().toISOString(),
    }]);
    setDraft(null);
  };

  const remove = (idx) => onChange(damages.filter((_, i) => i !== idx));

  return (
    <div data-testid={testId}>
      <p className="text-[10px] uppercase tracking-widest text-neutral-500">
        Damage inspection — tap the car to pin a mark
      </p>

      <div className="mt-2 grid grid-cols-2 gap-3">
        {VIEWS.map((v) => {
          const pins = damages.map((d, i) => ({ ...d, n: i + 1 })).filter((d) => d.zone === v.id);
          const old = existing.map((d, i) => ({ ...d, n: i + 1 })).filter((d) => d.zone === v.id);
          return (
            <div
              key={v.id}
              className="relative rounded border border-black/10 dark:border-white/15 bg-white dark:bg-[#111827] p-1 cursor-crosshair overflow-hidden"
              onClick={(e) => place(v.id, e)}
              onMouseMove={(e) => setHover({ view: v.id, ...coords(e) })}
              onMouseLeave={() => setHover(null)}
              onTouchStart={(e) => setHover({ view: v.id, ...coords(e) })}
              onTouchMove={(e) => setHover({ view: v.id, ...coords(e) })}
              onTouchEnd={() => setHover(null)}
              data-testid={`${testId}-view-${v.id}`}
            >
              <span className="absolute top-1 left-2 text-[9px] uppercase tracking-widest text-neutral-400">{v.label}</span>
              {hover?.view === v.id && (
                <span className="pointer-events-none" data-testid={`${testId}-crosshair`}>
                  <span className="absolute inset-y-0 w-px bg-sky-500/60" style={{ left: `${hover.x}%` }} />
                  <span className="absolute inset-x-0 h-px bg-sky-500/60" style={{ top: `${hover.y}%` }} />
                  <span
                    className="absolute w-3 h-3 -translate-x-1/2 -translate-y-1/2 rounded-full border border-sky-500 bg-sky-500/15"
                    style={{ left: `${hover.x}%`, top: `${hover.y}%` }}
                  />
                </span>
              )}
              <svg viewBox="0 0 200 120" className="w-full h-[88px] text-neutral-400 dark:text-neutral-500">
                <CarOutline view={v.id} />
              </svg>
              {old.map((d) => (
                <span
                  key={`old-${d.n}`}
                  style={{ left: `${d.x}%`, top: `${d.y}%` }}
                  title={`Existing: ${d.type} · ${d.severity}${d.notes ? ` — ${d.notes}` : ""}`}
                  className="absolute -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-amber-500 text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white"
                >
                  {d.n}
                </span>
              ))}
              {pins.map((d) => (
                <span
                  key={`new-${d.n}`}
                  style={{ left: `${d.x}%`, top: `${d.y}%` }}
                  title={`${d.type} · ${d.severity}${d.notes ? ` — ${d.notes}` : ""}`}
                  className="absolute -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-rose-600 text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white"
                  data-testid={`${testId}-pin-${d.n}`}
                >
                  {d.n}
                </span>
              ))}
            </div>
          );
        })}
      </div>

      {draft && (
        <div
          className="mt-3 p-3 rounded border border-rose-200 dark:border-rose-800/60 bg-rose-50 dark:bg-rose-900/20"
          data-testid={`${testId}-popover`}
        >
          <p className="text-[10px] uppercase tracking-widest text-rose-700 dark:text-rose-300">
            New mark on {VIEWS.find((v) => v.id === draft.view).label}
          </p>
          <div className="mt-2 grid grid-cols-2 gap-3">
            <select
              value={draft.type}
              onChange={(e) => setDraft({ ...draft, type: e.target.value })}
              className="bg-transparent border border-black/15 dark:border-white/15 rounded px-2 py-1.5 text-sm"
              data-testid={`${testId}-type`}
            >
              {TYPES.map((t) => <option key={t} value={t} className="text-black">{t}</option>)}
            </select>
            <select
              value={draft.severity}
              onChange={(e) => setDraft({ ...draft, severity: e.target.value })}
              className="bg-transparent border border-black/15 dark:border-white/15 rounded px-2 py-1.5 text-sm"
              data-testid={`${testId}-severity`}
            >
              {SEVERITIES.map((s) => <option key={s} value={s} className="text-black">{s}</option>)}
            </select>
          </div>
          <input
            value={draft.notes}
            onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            placeholder="Optional note (e.g. 10cm scratch below handle)"
            className="mt-2 w-full bg-transparent border border-black/15 dark:border-white/15 rounded px-2 py-1.5 text-sm"
            data-testid={`${testId}-notes`}
          />
          <div className="flex justify-end gap-2 mt-2">
            <button type="button" onClick={() => setDraft(null)} className="text-xs px-2.5 py-1.5 rounded border border-black/15 dark:border-white/15">
              Cancel
            </button>
            <button
              type="button"
              onClick={commit}
              className="text-xs px-2.5 py-1.5 rounded bg-rose-600 text-white hover:bg-rose-700"
              data-testid={`${testId}-save-pin`}
            >
              Add mark
            </button>
          </div>
        </div>
      )}

      {(existing.length > 0 || damages.length > 0) && (
        <ul className="mt-3 space-y-1.5 text-sm" data-testid={`${testId}-list`}>
          {existing.map((d, i) => (
            <li key={`e${i}`} className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
              <AlertTriangle size={13} className="shrink-0" />
              <span>#{i + 1} {VIEWS.find((v) => v.id === d.zone)?.label} · {d.type} · {d.severity}{d.notes ? ` — ${d.notes}` : ""} <em className="not-italic text-xs">(at handover)</em></span>
            </li>
          ))}
          {damages.map((d, i) => (
            <li key={`n${i}`} className="flex items-center gap-2">
              <span className="w-4 h-4 rounded-full bg-rose-600 text-white text-[9px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
              <span>{VIEWS.find((v) => v.id === d.zone)?.label} · {d.type} · {d.severity}{d.notes ? ` — ${d.notes}` : ""}</span>
              <button type="button" onClick={() => remove(i)} className="ml-auto text-neutral-400 hover:text-rose-600" data-testid={`${testId}-remove-${i + 1}`}>
                <X size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
